import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Target, Search, Plus, Edit2, Trash2, CheckCircle, XCircle, Clock,
  Users, Building2, Calendar, MapPin, Award, Eye, Play, Square,
  Filter, RefreshCw, Download
} from 'lucide-react';

const STATUS_COLORS = {
  'Aberta': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  'Em Curso': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  'Concluída': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  'Cancelada': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  'Suspensa': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
};

const GestaoMissoesPage = () => {
  const [missoes, setMissoes] = useState([]);
  const [stats, setStats] = useState({});
  const [instituicoes, setInstituicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterInstituicao, setFilterInstituicao] = useState('');
  const [selectedMissao, setSelectedMissao] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConcluirOpen, setIsConcluirOpen] = useState(false);
  const [editingMissao, setEditingMissao] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    instituicao_id: '',
    tipo_missao: 'Presencial',
    competencias_necessarias: [],
    numero_voluntarios_necessarios: 1,
    data_inicio: '',
    data_fim: '',
    localizacao: '',
    horas_estimadas: 0,
    notas_admin: ''
  });
  const [concluirData, setConcluirData] = useState({
    observacoes: '',
    horas_realizadas: 0
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterInstituicao]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [missoesRes, statsRes, instRes] = await Promise.all([
        apiClient.get('/admin/missoes', { params: { status: filterStatus || undefined, instituicao_id: filterInstituicao || undefined } }),
        apiClient.get('/admin/missoes/stats'),
        apiClient.get('/instituicoes')
      ]);
      setMissoes(missoesRes.data.missoes || []);
      setStats(statsRes.data);
      setInstituicoes(instRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (missao) => {
    try {
      const response = await apiClient.get(`/admin/missoes/${missao.id}`);
      setSelectedMissao(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMissao) {
        await apiClient.put(`/admin/missoes/${editingMissao.id}`, formData);
        toast.success('Missão atualizada com sucesso');
      } else {
        await apiClient.post('/admin/missoes', formData);
        toast.success('Missão criada com sucesso');
      }
      setIsFormOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao guardar missão');
    }
  };

  const handleEdit = (missao) => {
    setEditingMissao(missao);
    setFormData({
      titulo: missao.titulo || '',
      descricao: missao.descricao || '',
      instituicao_id: missao.instituicao_id || '',
      tipo_missao: missao.tipo_missao || 'Presencial',
      competencias_necessarias: missao.competencias_necessarias || [],
      numero_voluntarios_necessarios: missao.numero_voluntarios_necessarios || 1,
      data_inicio: missao.data_inicio || '',
      data_fim: missao.data_fim || '',
      localizacao: missao.localizacao || '',
      horas_estimadas: missao.horas_estimadas || 0,
      notas_admin: missao.notas_admin || ''
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (missaoId) => {
    if (!confirm('Tem a certeza que deseja eliminar esta missão?')) return;
    try {
      await apiClient.delete(`/admin/missoes/${missaoId}`);
      toast.success('Missão eliminada');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar missão');
    }
  };

  const handleConcluir = async () => {
    try {
      await apiClient.post(`/admin/missoes/${selectedMissao.id}/concluir`, concluirData);
      toast.success('Missão concluída! Os voluntários podem agora receber certificados.');
      setIsConcluirOpen(false);
      setIsDetailOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao concluir missão');
    }
  };

  const handleUpdateStatus = async (missaoId, newStatus) => {
    try {
      await apiClient.put(`/admin/missoes/${missaoId}`, { status: newStatus });
      toast.success(`Status atualizado para ${newStatus}`);
      fetchData();
      if (selectedMissao?.id === missaoId) {
        handleViewDetail({ id: missaoId });
      }
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleAceitarCandidatura = async (missaoId, candidaturaId) => {
    try {
      await apiClient.post(`/admin/portal/missoes/${missaoId}/candidaturas/${candidaturaId}/aceitar`);
      toast.success('Candidatura aceite');
      handleViewDetail({ id: missaoId });
    } catch (error) {
      toast.error('Erro ao aceitar candidatura');
    }
  };

  const handleRejeitarCandidatura = async (missaoId, candidaturaId) => {
    const motivo = prompt('Motivo da rejeição (opcional):');
    try {
      await apiClient.post(`/admin/portal/missoes/${missaoId}/candidaturas/${candidaturaId}/rejeitar`, { motivo });
      toast.success('Candidatura rejeitada');
      handleViewDetail({ id: missaoId });
    } catch (error) {
      toast.error('Erro ao rejeitar candidatura');
    }
  };

  const resetForm = () => {
    setEditingMissao(null);
    setFormData({
      titulo: '',
      descricao: '',
      instituicao_id: '',
      tipo_missao: 'Presencial',
      competencias_necessarias: [],
      numero_voluntarios_necessarios: 1,
      data_inicio: '',
      data_fim: '',
      localizacao: '',
      horas_estimadas: 0,
      notas_admin: ''
    });
  };

  const filteredMissoes = missoes.filter(m =>
    m.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.instituicao_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="gestao-missoes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Gestão de Missões</h1>
          <p className="text-slate-600 dark:text-slate-400">Controlar e gerir missões de voluntariado</p>
        </div>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="nova-missao-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Missão
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editingMissao ? 'Editar Missão' : 'Nova Missão'}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="titulo">Título *</Label>
                    <Input
                      id="titulo"
                      value={formData.titulo}
                      onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="descricao">Descrição</Label>
                    <Textarea
                      id="descricao"
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="instituicao_id">Instituição *</Label>
                    <Select value={formData.instituicao_id} onValueChange={(v) => setFormData({ ...formData, instituicao_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a instituição" />
                      </SelectTrigger>
                      <SelectContent>
                        {instituicoes.map(inst => (
                          <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tipo_missao">Tipo</Label>
                    <Select value={formData.tipo_missao} onValueChange={(v) => setFormData({ ...formData, tipo_missao: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Presencial">Presencial</SelectItem>
                        <SelectItem value="Remoto">Remoto</SelectItem>
                        <SelectItem value="Híbrido">Híbrido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="data_inicio">Data Início</Label>
                    <Input
                      id="data_inicio"
                      type="date"
                      value={formData.data_inicio}
                      onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="data_fim">Data Fim</Label>
                    <Input
                      id="data_fim"
                      type="date"
                      value={formData.data_fim}
                      onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="localizacao">Localização</Label>
                    <Input
                      id="localizacao"
                      value={formData.localizacao}
                      onChange={(e) => setFormData({ ...formData, localizacao: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="horas_estimadas">Horas Estimadas</Label>
                    <Input
                      id="horas_estimadas"
                      type="number"
                      value={formData.horas_estimadas}
                      onChange={(e) => setFormData({ ...formData, horas_estimadas: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="numero_voluntarios">Nº Voluntários Necessários</Label>
                    <Input
                      id="numero_voluntarios"
                      type="number"
                      value={formData.numero_voluntarios_necessarios}
                      onChange={(e) => setFormData({ ...formData, numero_voluntarios_necessarios: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
                <Button type="submit">{editingMissao ? 'Atualizar' : 'Criar'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total || 0}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.abertas || 0}</p>
              <p className="text-xs text-slate-500">Abertas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.em_curso || 0}</p>
              <p className="text-xs text-slate-500">Em Curso</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.concluidas || 0}</p>
              <p className="text-xs text-slate-500">Concluídas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-pink-600">{stats.total_voluntarios_envolvidos || 0}</p>
              <p className="text-xs text-slate-500">Voluntários</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.total_horas_voluntariado || 0}h</p>
              <p className="text-xs text-slate-500">Total Horas</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar missões..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Aberta">Aberta</SelectItem>
                <SelectItem value="Em Curso">Em Curso</SelectItem>
                <SelectItem value="Concluída">Concluída</SelectItem>
                <SelectItem value="Cancelada">Cancelada</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setSearchTerm(''); setFilterStatus(''); setFilterInstituicao(''); fetchData(); }}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Missions List */}
      <Card>
        <CardHeader>
          <CardTitle>Missões</CardTitle>
          <CardDescription>{filteredMissoes.length} missões encontradas</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin" />
            </div>
          ) : filteredMissoes.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma missão encontrada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMissoes.map(missao => (
                <div
                  key={missao.id}
                  className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#0066CC]/10 flex items-center justify-center">
                      <Target className="w-6 h-6 text-[#0066CC]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900 dark:text-white">{missao.titulo}</h3>
                        <Badge className={STATUS_COLORS[missao.status] || 'bg-slate-100 text-slate-800'}>
                          {missao.status}
                        </Badge>
                        <Badge variant="outline">{missao.tipo_missao}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {missao.instituicao_nome}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {missao.voluntarios_aceites || 0}/{missao.numero_voluntarios_necessarios} voluntários
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {missao.horas_estimadas}h
                        </span>
                        {missao.data_inicio && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(missao.data_inicio).toLocaleDateString('pt-PT')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleViewDetail(missao)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(missao)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {missao.status !== 'Concluída' && missao.status !== 'Cancelada' && (
                      <Button variant="outline" size="sm" onClick={() => handleDelete(missao.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedMissao?.titulo}
              <Badge className={STATUS_COLORS[selectedMissao?.status] || ''}>
                {selectedMissao?.status}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {selectedMissao?.instituicao?.nome} - {selectedMissao?.tipo_missao}
            </DialogDescription>
          </DialogHeader>

          {selectedMissao && (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Informação</TabsTrigger>
                <TabsTrigger value="voluntarios">Voluntários ({selectedMissao.stats?.total_candidaturas || 0})</TabsTrigger>
                <TabsTrigger value="acoes">Ações</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500">Período</p>
                    <p className="font-medium dark:text-white">
                      {selectedMissao.data_inicio ? new Date(selectedMissao.data_inicio).toLocaleDateString('pt-PT') : 'N/A'}
                      {' - '}
                      {selectedMissao.data_fim ? new Date(selectedMissao.data_fim).toLocaleDateString('pt-PT') : 'N/A'}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500">Localização</p>
                    <p className="font-medium dark:text-white">{selectedMissao.localizacao || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500">Horas Estimadas</p>
                    <p className="font-medium dark:text-white">{selectedMissao.horas_estimadas}h</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500">Voluntários</p>
                    <p className="font-medium dark:text-white">
                      {selectedMissao.stats?.aceites || 0}/{selectedMissao.numero_voluntarios_necessarios} confirmados
                    </p>
                  </div>
                </div>
                {selectedMissao.descricao && (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Descrição</p>
                    <p className="text-sm dark:text-white">{selectedMissao.descricao}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="voluntarios" className="space-y-3">
                {selectedMissao.candidaturas?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhuma candidatura</p>
                  </div>
                ) : (
                  selectedMissao.candidaturas?.map(cand => (
                    <div key={cand.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium dark:text-white">{cand.voluntario?.nome}</p>
                        <p className="text-sm text-slate-500">{cand.voluntario?.email}</p>
                        <div className="flex gap-1 mt-1">
                          {cand.voluntario?.competencias?.slice(0, 3).map(c => (
                            <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          cand.status === 'aceite' ? 'bg-green-100 text-green-800' :
                          cand.status === 'concluido' ? 'bg-purple-100 text-purple-800' :
                          cand.status === 'rejeitada' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }>
                          {cand.status}
                        </Badge>
                        {cand.status === 'pendente' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleAceitarCandidatura(selectedMissao.id, cand.id)}>
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRejeitarCandidatura(selectedMissao.id, cand.id)}>
                              <XCircle className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </TabsContent>

              <TabsContent value="acoes" className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {selectedMissao.status === 'Aberta' && (
                    <Button onClick={() => handleUpdateStatus(selectedMissao.id, 'Em Curso')} className="bg-blue-600">
                      <Play className="w-4 h-4 mr-2" />
                      Iniciar Missão
                    </Button>
                  )}
                  {selectedMissao.status === 'Em Curso' && (
                    <>
                      <Button onClick={() => { setConcluirData({ observacoes: '', horas_realizadas: selectedMissao.horas_estimadas }); setIsConcluirOpen(true); }} className="bg-purple-600">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Concluir Missão
                      </Button>
                      <Button variant="outline" onClick={() => handleUpdateStatus(selectedMissao.id, 'Suspensa')}>
                        <Square className="w-4 h-4 mr-2" />
                        Suspender
                      </Button>
                    </>
                  )}
                  {selectedMissao.status === 'Suspensa' && (
                    <Button onClick={() => handleUpdateStatus(selectedMissao.id, 'Em Curso')} className="bg-blue-600">
                      <Play className="w-4 h-4 mr-2" />
                      Retomar
                    </Button>
                  )}
                  {selectedMissao.status !== 'Concluída' && selectedMissao.status !== 'Cancelada' && (
                    <Button variant="destructive" onClick={() => handleUpdateStatus(selectedMissao.id, 'Cancelada')}>
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancelar Missão
                    </Button>
                  )}
                  {selectedMissao.status === 'Concluída' && (
                    <div className="col-span-2 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                      <Award className="w-8 h-8 mx-auto text-green-600 mb-2" />
                      <p className="text-green-700 dark:text-green-400 font-medium">
                        Missão concluída! Os certificados podem ser emitidos na página de Certificados.
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Concluir Modal */}
      <Dialog open={isConcluirOpen} onOpenChange={setIsConcluirOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Concluir Missão</DialogTitle>
            <DialogDescription>
              Ao concluir a missão, os voluntários aceites serão marcados como concluídos e poderão receber certificados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Horas Realizadas</Label>
              <Input
                type="number"
                value={concluirData.horas_realizadas}
                onChange={(e) => setConcluirData({ ...concluirData, horas_realizadas: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                value={concluirData.observacoes}
                onChange={(e) => setConcluirData({ ...concluirData, observacoes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConcluirOpen(false)}>Cancelar</Button>
            <Button onClick={handleConcluir} className="bg-purple-600">
              <CheckCircle className="w-4 h-4 mr-2" />
              Concluir Missão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestaoMissoesPage;
